/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import {useTranslation} from 'react-i18next'
import {PencilIcon, TrashIcon} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import {UiTableCell, UiTableRow} from 'components/Ui/UiTable'
import UiIconButton from 'components/Ui/UiIconButton'
import type {LeadNote} from 'types/entities/LeadNote'
import Dates from 'utils/Dates'
import {DATE_FORMATS} from 'constants/DateFormats'

interface EditLeadModalNoteItemProps {
    note: LeadNote
    onEdit: (note: LeadNote) => void
    onDelete: (note: LeadNote) => void
}

const EditLeadModalNoteItem: React.FC<EditLeadModalNoteItemProps> = ({ note, onEdit, onDelete }) => {
    const { t } = useTranslation()

    return (
        <>
            <UiTableRow className="EditLeadModalNoteItem group/note w-full">
                <UiTableCell colSpan={3} className={'w-full'}>
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                                    {t(note.type)}
                                </span>
                                <span className="text-[10px] text-gray-500 dark:text-gray-500">
                                    {Dates.dayjs(note.created_at).format(DATE_FORMATS.DATE_TIME_DOT)}
                                </span>
                            </div>
                            {!note.is_permanent && (
                                <div className="flex items-center justify-end gap-1">
                                    <UiIconButton
                                        onClick={() => onEdit(note)}
                                        className={clsx('text-blue-600 hover:text-blue-900')}
                                        title={t('Edit') as string}
                                        variant={'light'}
                                        size={'sm'}
                                    >
                                        <PencilIcon className={clsx('w-5 h-5')} />
                                    </UiIconButton>
                                    <UiIconButton
                                        onClick={() => onDelete(note)}
                                        className={clsx('text-red-600 hover:text-red-900')}
                                        title={t('Delete') as string}
                                        variant={'light'}
                                        size={'sm'}
                                    >
                                        <TrashIcon className={clsx('w-5 h-5')} />
                                    </UiIconButton>
                                </div>
                            )}
                        </div>
                        <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap wrap-break-word max-h-62.5 overflow-y-auto">
                            {note.content}
                        </div>
                    </div>
                </UiTableCell>
            </UiTableRow>
        </>
    )
}

export default EditLeadModalNoteItem
